"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  formatRationalNice,
  type Rational,
} from "@/lib/tool-kit/math/rational";
import {
  MAX_NEXT,
  MAX_TERMS,
  parseRationalList,
  type RationalList,
  type RuleKind,
  type SequenceRule,
  solveSequence,
} from "@/lib/tool-kit/number-theory/sequences";

/** English strings. A translated content file overrides these through `ui`. */
export const NUMBER_SEQUENCE_SOLVER_STRINGS = {
  sequence: "Your sequence",
  sequenceHint:
    "Numbers, decimals or fractions (1/2) separated by commas or spaces. Use semicolons between terms if you write decimals with a comma. 3 to {max} terms.",
  sequencePlaceholder: "2, 6, 12, 20, 30",
  nextCount: "How many next terms",
  nextHint: "1 to {max}.",
  results: "Results",
  ruleFound: "Rule found",
  nextTerm: "Next term",
  termsUsed: "Terms entered",
  kindArithmetic: "Arithmetic sequence",
  kindGeometric: "Geometric sequence",
  kindPolynomial: "Polynomial of degree {degree}",
  kindAffine: "Recurrence with a constant",
  kindRecurrence: "Second-order recurrence",
  explainArithmetic: "Each term is the one before it plus {d}.",
  explainGeometric: "Each term is the one before it times {r}.",
  explainPolynomial:
    "The differences at level {degree} are all equal, so the terms follow a polynomial of degree {degree} in n.",
  explainAffine: "Each term is {r} times the one before it, plus {c}.",
  explainRecurrence:
    "Each term is {p} times the one before it plus {q} times the one before that.",
  fibonacciLike:
    "That is the Fibonacci rule: each term is the sum of the previous two.",
  formulaHeading: "The rule",
  nthTerm: "n-th term",
  recurrence: "Recurrence",
  nextHeading: "Next terms",
  nextLine: "a{index} = {value}",
  copyNext: "Copy next terms",
  copied: "Copied!",
  tableHeading: "Difference table",
  tableIntro:
    "Subtract each term from the next to get the first differences, then repeat. When a row is constant the terms follow a polynomial of that degree.",
  colLevel: "Level",
  rowTerms: "Terms",
  rowDifferences: "Differences, level {level}",
  constantRow: "(constant)",
  noRule:
    "No simple rule found. Nothing in the list fits an arithmetic, geometric, polynomial or recurrence rule with a term left over to check it.",
  needMore:
    "A rule can only be confirmed with a spare term: 3 terms for an arithmetic or geometric rule, 4 for a quadratic or a recurrence with a constant, 5 for a Fibonacci-style rule. More terms may reveal one.",
  honesty:
    "This tool finds a rule that fits, not the rule. Any list is consistent with infinitely many rules; it reports the simplest kinds, and only those confirmed by a spare term.",
  enter: "Enter at least 3 terms to look for a rule.",
  tooFew: "Enter at least 3 terms. Two numbers fit far too many rules.",
  invalid:
    "These are not numbers: {tokens}. Use whole numbers, decimals or fractions like 1/2.",
  tooMany: "Enter at most {max} terms.",
  tooLong: "Each number can have at most 30 digits.",
  badNext: "Choose between 1 and {max} next terms.",
} as const;

type Strings = typeof NUMBER_SEQUENCE_SOLVER_STRINGS;

const MONO_LINES =
  "space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";
const PROSE_SM = "text-muted-foreground text-sm";
const MAX_TABLE_LEVELS = 6;
const DEFAULT_NEXT = 5;
const SUBSCRIPT_DIGITS = "₀₁₂₃₄₅₆₇₈₉";

/** 12 → "₁₂", so a₁₂ reads as a term label whatever the locale's digits. */
function subscript(n: number): string {
  return [...String(n)].map((d) => SUBSCRIPT_DIGITS[Number(d)]).join("");
}

function decimalMarkFor(locale: string): string {
  const part = new Intl.NumberFormat(locale)
    .formatToParts(1.1)
    .find((p) => p.type === "decimal");
  return part?.value ?? ".";
}

function kindLabel(rule: SequenceRule, strings: Strings): string {
  const labels: Record<RuleKind, string> = {
    arithmetic: strings.kindArithmetic,
    geometric: strings.kindGeometric,
    polynomial: t(strings.kindPolynomial, { degree: String(rule.degree) }),
    affine: strings.kindAffine,
    recurrence: strings.kindRecurrence,
  };
  return labels[rule.kind];
}

function explain(rule: SequenceRule, strings: Strings): string {
  const v = rule.values;
  const templates: Record<RuleKind, string> = {
    arithmetic: strings.explainArithmetic,
    geometric: strings.explainGeometric,
    polynomial: strings.explainPolynomial,
    affine: strings.explainAffine,
    recurrence: strings.explainRecurrence,
  };
  const text = t(templates[rule.kind], { ...v, degree: String(rule.degree) });
  const fibonacci = rule.kind === "recurrence" && v.p === "1" && v.q === "1";
  return fibonacci ? `${text} ${strings.fibonacciLike}` : text;
}

function DifferenceTable({
  rows,
  rule,
  show,
  strings,
}: {
  rows: Rational[][];
  rule: SequenceRule | undefined;
  show: (value: Rational) => string;
  strings: Strings;
}) {
  const levels = rows.slice(0, MAX_TABLE_LEVELS + 1);
  const width = rows[0].length;
  const head = [
    strings.colLevel,
    ...Array.from({ length: width }, (_, i) => `a${subscript(i + 1)}`),
  ];
  const body = levels.map((row, level) => {
    const label =
      level === 0
        ? strings.rowTerms
        : t(strings.rowDifferences, { level: String(level) });
    const constant = rule?.degree === level && level > 0;
    return [
      constant ? `${label} ${strings.constantRow}` : label,
      ...row.map(show),
    ];
  });
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <p className={PROSE_SM}>{strings.tableIntro}</p>
      <DataTable head={head} rows={body} />
    </>
  );
}

function RuleBlock({
  rule,
  start,
  show,
  strings,
}: {
  rule: SequenceRule;
  start: number;
  show: (value: Rational) => string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const nextTexts = rule.next.map(show);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.ruleFound}
          value={kindLabel(rule, strings)}
        />
        <StatCard label={strings.nextTerm} value={nextTexts[0]} />
        <StatCard label={strings.termsUsed} value={fmt(start)} />
      </StatGrid>
      <p className="text-foreground text-sm">{explain(rule, strings)}</p>
      <h3 className={WIDGET_H3}>{strings.formulaHeading}</h3>
      <div className={MONO_LINES}>
        {rule.formula ? (
          <p>
            {strings.nthTerm}: {rule.formula}
          </p>
        ) : null}
        {rule.recurrence ? (
          <p>
            {strings.recurrence}: {rule.recurrence}
          </p>
        ) : null}
      </div>
      <h3 className={WIDGET_H3}>{strings.nextHeading}</h3>
      <div className={MONO_LINES}>
        {nextTexts.map((text, index) => (
          // The position is the identity of a predicted term.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional terms
          <p key={index}>
            {t(strings.nextLine, {
              index: fmt(start + 1 + index),
              value: text,
            })}
          </p>
        ))}
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyNext}
        small
        text={nextTexts.join(", ")}
      />
    </>
  );
}

function Solved({
  list,
  nextCount,
  strings,
}: {
  list: RationalList;
  nextCount: number;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const mark = useMemo(() => decimalMarkFor(locale), [locale]);
  const show = (value: Rational) => formatRationalNice(value, mark);
  const result = useMemo(
    () => solveSequence(list.values, nextCount),
    [list.values, nextCount]
  );
  const rule = result.rules[0];
  return (
    <>
      {rule ? (
        <RuleBlock
          rule={rule}
          show={show}
          start={list.values.length}
          strings={strings}
        />
      ) : (
        <>
          <Notice>{strings.noRule}</Notice>
          <p className={PROSE_SM}>{strings.needMore}</p>
        </>
      )}
      <DifferenceTable
        rows={result.differences}
        rule={rule}
        show={show}
        strings={strings}
      />
      <p className={PROSE_SM}>{strings.honesty}</p>
    </>
  );
}

function Outcome({
  list,
  nextCount,
  strings,
}: {
  list: RationalList;
  nextCount: number | null;
  strings: Strings;
}) {
  if (list.invalid.length > 0) {
    return (
      <ErrorNotice>
        {t(strings.invalid, { tokens: list.invalid.join(", ") })}
      </ErrorNotice>
    );
  }
  if (list.tooLong) {
    return <ErrorNotice>{strings.tooLong}</ErrorNotice>;
  }
  if (list.tooMany) {
    return <ErrorNotice>{t(strings.tooMany, { max: MAX_TERMS })}</ErrorNotice>;
  }
  if (nextCount === null) {
    return <ErrorNotice>{t(strings.badNext, { max: MAX_NEXT })}</ErrorNotice>;
  }
  if (list.values.length === 0) {
    return <Notice>{strings.enter}</Notice>;
  }
  if (list.values.length < 3) {
    return <Notice>{strings.tooFew}</Notice>;
  }
  return <Solved list={list} nextCount={nextCount} strings={strings} />;
}

export default function NumberSequenceSolverWidget() {
  const strings = useToolUi(NUMBER_SEQUENCE_SOLVER_STRINGS);
  const fmt = useToolNumber();
  const [raw, setRaw] = useState("2, 6, 12, 20, 30");
  const [rawNext, setRawNext] = useState(String(DEFAULT_NEXT));
  const list = useMemo(() => parseRationalList(raw), [raw]);
  const parsedNext = parseIntegerInput(rawNext);
  const nextCount =
    parsedNext !== null && parsedNext >= 1 && parsedNext <= MAX_NEXT
      ? parsedNext
      : null;
  return (
    <>
      <TextAreaField
        hint={t(strings.sequenceHint, { max: fmt(MAX_TERMS) })}
        id="nss-sequence"
        label={strings.sequence}
        onChange={setRaw}
        placeholder={strings.sequencePlaceholder}
        rows={3}
        value={raw}
      />
      <TextField
        hint={t(strings.nextHint, { max: fmt(MAX_NEXT) })}
        id="nss-next"
        label={strings.nextCount}
        mono
        numeric
        onChange={setRawNext}
        value={rawNext}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome list={list} nextCount={nextCount} strings={strings} />
      </section>
    </>
  );
}
